// De offerte van het mandje, als één stand voor het eenscherm (15-09).
//
// Regel van deze ronde: het portaal rekent niets. Alles wat het scherm
// aan bedragen toont komt uit `/offerte`, en die wordt opnieuw gehaald
// bij élke wijziging van aantal, adres, bezorgkeuze, betaalmethode of
// kortingscode. Deze hook bewaakt dat, met drie harde eisen uit de mock:
//
// 1. **Nooit een stille wachtstand.** Zodra de invoer verandert staat de
//    stand meteen op `bezig` — nog vóór het verzoek vertrekt. De pagina
//    grijst Bezorging en Totaal, en de betaalknop gaat uit.
// 2. **Nooit een oud bedrag afrekenen.** Een antwoord dat bij een
//    inmiddels achterhaalde invoer hoort wordt weggegooid (de `sleutel`
//    van het antwoord moet die van nú zijn); tot er een verse offerte is
//    blijft de knop uit. De `afdruk` die meegaat naar `/betalen` hoort
//    altijd bij het bedrag dat op dat moment in beeld staat.
// 3. **Nooit eindeloos wachten, en één stille herpoging.** De
//    axios-timeout zit op `HERBESTEL_OFFERTE_TIMEOUT_MS`. Loopt een
//    verzoek stuk, dan volgt er na `OFFERTE_HERKANSING_MS` nog één
//    poging zonder dat het scherm iets zegt; pas als die ook faalt
//    staat de stand op `fout` en toont de pagina "Dat lukte even niet"
//    met een knop Opnieuw proberen.
//
// Een verzoek dat door een nieuwere invoer achterhaald is wordt
// *niet* afgebroken, maar genegeerd (15-09, vierde ronde). Afbreken
// leek netter en was het niet: een halverwege afgekapte XHR komt in het
// netwerkpaneel van Chrome terecht als een mislukt verzoek -- JP zag
// daar drie snelle tikken lang "twee 503's en dan een 200", terwijl de
// server op precies diezelfde tikken niets anders deed dan 200
// antwoorden (gemeten 15-09 op productie: geen enkele `503` in
// `request.afgehandeld`, geen enkele mislukte offerte in de metingen).
// Wie nooit afbreekt, laat de grendel op de server zijn werk doen: die
// serialiseert de aanvragen per link, geeft de ingehaalde een nette 409
// `offerte_achterhaald` (stil) en de laatste een 200 met de eindstand.
// Alleen als de pagina zelf weggaat wordt er nog afgebroken.

import { useCallback, useEffect, useRef, useState } from "react";

import { herbestelApi } from "@/lib/api";
import type {
  HerbestelAnderAdres,
  HerbestelOfferteAntwoord,
} from "@/types/api";

/**
 * Rust tussen de laatste toetsaanslag of tik en het verzoek. Kort genoeg
 * dat het scherm meteen reageert, lang genoeg dat drie tikken op "+"
 * één offerte opleveren in plaats van drie.
 */
export const OFFERTE_RUST_MS = 400;

/**
 * Eén stille herpoging voordat "Dat lukte even niet" verschijnt (15-09,
 * tweede ronde). Een offerte die op een losse hapering strandt kost de
 * klant zo een seconde extra in plaats van een rode melding en een knop.
 * Mislukt ook de herpoging, dan is het een echte storing en zegt de
 * pagina dat gewoon — de melding en de knop Opnieuw proberen blijven.
 */
export const OFFERTE_HERKANSING_MS = 1000;

export type OfferteStand = "leeg" | "bezig" | "klaar" | "fout";

export interface OfferteInvoer {
  regels: { vid: string; aantal: number }[];
  combidealVid: string | null;
  adresKeuze: "klant" | "ander";
  adres: HerbestelAnderAdres | null;
  methode: string;
  bezorgoptie: string | null;
  /** De titel van de gekozen vervoerder (hotfix 17-09). */
  bezorgvervoerder: string | null;
  kortingscode: string | null;
}

export interface OfferteResultaat {
  stand: OfferteStand;
  offerte: HerbestelOfferteAntwoord | null;
  /** 409 op /betalen: het bekende adres mist een verplicht gegeven. */
  adresOnbekend: boolean;
  /** Opnieuw proberen na een fout, of een verse offerte afdwingen. */
  opnieuw: () => void;
}

/**
 * De invoer als vergelijkbare sleutel. Twee invoeren die hierop gelijk
 * zijn leveren dezelfde offerte op, dus dan hoeft er niets te gebeuren —
 * en een antwoord hoort alleen bij de sleutel waarvoor het gevraagd is.
 */
export function offerteSleutel(invoer: OfferteInvoer | null): string {
  if (!invoer) return "";
  return JSON.stringify({
    regels: invoer.regels
      .map((r) => `${r.vid}:${r.aantal}`)
      .slice()
      .sort(),
    combideal: invoer.combidealVid ?? "",
    keuze: invoer.adresKeuze,
    adres: invoer.adresKeuze === "ander" ? invoer.adres : null,
    methode: invoer.methode,
    bezorgoptie: invoer.bezorgoptie ?? "",
    vervoerder: invoer.bezorgvervoerder ?? "",
    code: invoer.kortingscode ?? "",
  });
}

/**
 * `invoer` is `null` zolang er niets te offreren valt (geen product
 * gekozen, of het afrekenen staat uit). De stand is dan `leeg` en de
 * pagina toont geen bedragen — geen oude offerte die blijft staan.
 */
export function useOfferte(
  token: string | null,
  shopToken: string | null,
  invoer: OfferteInvoer | null,
  opFout?: (soort: "fout" | "adres_onbekend") => void,
): OfferteResultaat {
  const [stand, setStand] = useState<OfferteStand>("leeg");
  const [offerte, setOfferte] = useState<HerbestelOfferteAntwoord | null>(
    null,
  );
  const [adresOnbekend, setAdresOnbekend] = useState(false);
  // Hoogt op bij elke "Opnieuw proberen"; dwingt het effect opnieuw af
  // ook als de invoer identiek is.
  const [poging, setPoging] = useState(0);

  const sleutel = offerteSleutel(invoer);
  // De invoer en de foutmelder in refs: het effect hangt aan de
  // sleutel, niet aan het object — anders vuurt hij op elke render
  // opnieuw. Ze worden in een eigen effect bijgewerkt en nooit tijdens
  // het renderen aangeraakt (react-hooks/refs).
  const invoerRef = useRef(invoer);
  const opFoutRef = useRef(opFout);
  useEffect(() => {
    invoerRef.current = invoer;
    opFoutRef.current = opFout;
  });

  // Alle verzoeken die nog onderweg zijn. Ze worden alléén afgebroken
  // als de pagina verdwijnt; een achterhaald verzoek loopt gewoon uit
  // en zijn antwoord wordt genegeerd.
  const lopendRef = useRef<AbortController[]>([]);
  useEffect(
    () => () => {
      lopendRef.current.forEach((c) => c.abort());
      lopendRef.current = [];
    },
    [],
  );

  useEffect(() => {
    const huidig = invoerRef.current;
    if (!huidig) {
      setStand("leeg");
      setOfferte(null);
      return;
    }

    // Meteen in de wachtstand, vóór de rusttijd: zolang er geen verse
    // offerte is mag er niet afgerekend worden. Het oude bedrag blijft
    // staan (grijs), zodat het scherm niet leegspringt.
    setStand("bezig");

    const controller = new AbortController();
    lopendRef.current.push(controller);
    // Staat er inmiddels een nieuwere invoer? Dan hoort dit antwoord
    // nergens meer bij en doet het niets -- geen melding, geen bedrag,
    // geen herkansing.
    let achterhaald = false;
    const klaarMetVerzoek = () => {
      lopendRef.current = lopendRef.current.filter((c) => c !== controller);
    };
    // Beide timers in één lijst: de opruimer van dit effect zet ze
    // allemaal stil, zodat een herpoging nooit doorloopt op invoer die
    // alweer achterhaald is.
    const timers: number[] = [];

    const vraag = (herkansing: boolean) => {
      herbestelApi
        .offerte(token, shopToken, huidig, controller.signal)
        .then((antwoord) => {
          klaarMetVerzoek();
          if (achterhaald || controller.signal.aborted) return;
          setOfferte(antwoord);
          setAdresOnbekend(false);
          setStand("klaar");
        })
        .catch((fout: unknown) => {
          klaarMetVerzoek();
          if (achterhaald || controller.signal.aborted) return;
          // Een afgebroken verzoek (nieuwe invoer, pagina weg) is geen
          // fout: die zou een rode melding tonen op een scherm dat al
          // aan het verversen is.
          if ((fout as { code?: string } | null)?.code === "ERR_CANCELED") {
            return;
          }
          const respons = (
            fout as {
              response?: { status?: number; data?: { detail?: string } };
            } | null
          )?.response;
          const status = respons?.status;
          if (status === 409 && respons?.data?.detail === "offerte_achterhaald") {
            // Er liep al een nieuwere aanvraag voor deze link; de server
            // heeft deze overgeslagen (15-09, derde ronde). Stil laten
            // vallen: de stand blijft `bezig`, het laatst bevestigde
            // bedrag blijft grijs staan en de nieuwere aanvraag vult hem
            // zo in. Nooit "Dat lukte even niet" voor iets wat goed gaat.
            return;
          }
          if (status === 409 && huidig.adresKeuze === "klant") {
            // Het bekende adres mist een verplicht gegeven (doorgaans
            // het telefoonnummer). De pagina schakelt naar de invulweg;
            // dit is geen storing.
            setAdresOnbekend(true);
            setStand("leeg");
            setOfferte(null);
            opFoutRef.current?.("adres_onbekend");
            return;
          }
          if (!herkansing) {
            // Eén stille herpoging: de stand blijft `bezig`, er
            // verschijnt geen melding en de betaalknop blijft uit.
            timers.push(
              window.setTimeout(() => vraag(true), OFFERTE_HERKANSING_MS),
            );
            return;
          }
          setStand("fout");
          opFoutRef.current?.("fout");
        });
    };

    timers.push(window.setTimeout(() => vraag(false), OFFERTE_RUST_MS));

    return () => {
      // De geplande poging gaat niet meer weg, en een poging die al
      // onderweg is loopt uit zonder nog iets te doen. Niet afbreken:
      // zie de toelichting boven aan dit bestand.
      timers.forEach((t) => window.clearTimeout(t));
      achterhaald = true;
    };
    // `sleutel` vat de hele invoer samen; `poging` forceert een herhaling.
  }, [sleutel, poging, token, shopToken]);

  const opnieuw = useCallback(() => setPoging((n) => n + 1), []);

  return { stand, offerte, adresOnbekend, opnieuw };
}
